import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { FILE_URL } from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSearch, FaFilter, FaTrash, FaPlus, FaEdit, FaBoxOpen, FaGripVertical, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const Products = () => {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const itemsPerPage = 10;
    const [draggedItem, setDraggedItem] = useState(null);

    const fetchProducts = async () => {
        try {
            const res = await axios.get('/products');
            setProducts(res.data);
        } catch (error) {
            console.error('Ürünler yüklenemedi');
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get('/categories');
            setCategories(res.data);
        } catch (error) {
            console.error('Kategoriler yüklenemedi');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchProducts(), fetchCategories()]);
            setLoading(false);
        };
        loadData();
    }, []);

    // Filtre değişince sayfayı başa al
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategory]);

    const handleDelete = async (id) => {
        if (window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) {
            try {
                await axios.delete(`/products/${id}`);
                toast.success('Ürün silindi.');
                fetchProducts();
            } catch (error) {
                toast.error('Silme işlemi başarısız.');
            }
        }
    };

    // Toplu Silme
    const handleBulkDelete = async () => {
        if (selectedProducts.length === 0) return;
        
        if (window.confirm(`${selectedProducts.length} adet ürünü silmek istediğinize emin misiniz?`)) {
            try {
                await axios.post('/products/delete-bulk', { ids: selectedProducts });
                toast.success('Seçilen ürünler silindi.');
                setSelectedProducts([]);
                fetchProducts();
            } catch (error) {
                toast.error('Toplu silme işlemi başarısız.');
            }
        }
    };

    // Durum Değiştir (Aktif/Pasif)
    const handleStatusChange = async (id, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await axios.put(`/products/${id}/status`, { is_active: newStatus });
            setProducts(prev => prev.map(p => p.id === id ? { ...p, is_active: newStatus } : p));
            toast.success(`Ürün ${newStatus ? 'aktif' : 'pasif'} yapıldı.`);
        } catch (error) {
            toast.error('Durum güncellenemedi.');
        }
    };

    // Seçim İşlemleri
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = filteredProducts.map(p => p.id);
            setSelectedProducts(allIds);
        } else {
            setSelectedProducts([]);
        }
    };

    const handleSelect = (id) => {
        if (selectedProducts.includes(id)) {
            setSelectedProducts(selectedProducts.filter(pid => pid !== id));
        } else {
            setSelectedProducts([...selectedProducts, id]);
        }
    };

    // Filtreleme Mantığı
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory 
            ? product.categories?.some(c => c.id === Number(selectedCategory))
            : true;
        return matchesSearch && matchesCategory;
    });

    // Sayfalama Mantığı
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // --- Sürükle Bırak İşlemleri ---
    const onDragStart = (e, index) => {
        setDraggedItem(products[index]);
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
    };

    const onDrop = async (e, targetIndex) => {
        e.preventDefault();
        
        // Eğer filtreleme varsa sıralamaya izin verme (kafa karışıklığını önlemek için)
        if (searchTerm || selectedCategory) {
            toast.warning('Filtreleme etkinken sıralama yapılamaz.');
            return;
        }

        const draggedItemIndex = products.findIndex(p => p.id === draggedItem.id);
        if (draggedItemIndex === targetIndex) return;

        const newProducts = [...products];
        const [removed] = newProducts.splice(draggedItemIndex, 1);
        newProducts.splice(targetIndex, 0, removed);

        setProducts(newProducts); // Arayüzü güncelle

        try {
            const ids = newProducts.map(p => p.id);
            await axios.put('/products/reorder/all', { ids });
            toast.success('Sıralama güncellendi.');
            // toast.success('Sıralama güncellendi.'); // Çok sık çıkmasın diye kapalı
        } catch (error) {
            toast.error('Sıralama kaydedilemedi.');
        }
        setDraggedItem(null);
    };

    return (
        <AdminLayout>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h2 className="fw-bold mb-0">{t('admin_products.title')}</h2>
                    <p className="text-muted small mb-0">{t('admin_products.subtitle')}</p>
                </div>
                <div className="d-flex gap-2 align-self-start align-self-md-auto">
                    {selectedProducts.length > 0 && (
                        <button onClick={handleBulkDelete} className="btn btn-danger shadow-sm">
                            <FaTrash className="me-2" /> {t('admin_products.delete_selected')} ({selectedProducts.length})
                        </button>
                    )}
                    <Link to="/admin/products/new" className="btn btn-primary shadow-sm">
                        <FaPlus className="me-2" /> {t('admin_products.add_new')}
                    </Link>
                </div>
            </div>

            {/* Filtreleme Alanı */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-5">
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0"><FaSearch className="text-muted" /></span>
                                <input 
                                    type="text" 
                                    className="form-control border-start-0 bg-light" 
                                    placeholder={t('admin_products.search_placeholder')} 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0"><FaFilter className="text-muted" /></span>
                                <select 
                                    className="form-select border-start-0 bg-light" 
                                    value={selectedCategory} 
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    <option value="">{t('admin_products.all_categories')}</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                         <div className="col-md-3 text-end">
                            <button 
                                className="btn btn-light text-muted w-100"
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('');
                                }}
                            >
                                {t('admin_products.clear_filters')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="table-responsive d-none d-lg-block">
                    <table className="table table-hover align-middle mb-0" style={{minWidth: '1200px'}}>
                        <thead className="bg-light">
                            <tr>
                                <th style={{width: '30px'}}></th>
                                <th style={{width: '40px'}} className="ps-4">
                                    <div className="form-check">
                                    <input 
                                        type="checkbox" 
                                        className="form-check-input"
                                        onChange={handleSelectAll}
                                        checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                                    />
                                    </div>
                                </th>
                                <th style={{width: '80px'}}>{t('admin_products.image')}</th>
                                <th>{t('admin_products.product_name')}</th>
                                <th>{t('admin_products.categories')}</th>
                                <th>{t('admin_products.unit')}</th>
                                <th>{t('admin_products.variation_count')}</th>
                                <th>{t('admin_products.total_stock')}</th>
                                <th>Durum</th>
                                <th className="text-end pe-4">{t('admin_products.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">{t('common.loading')}</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {currentItems.map((product, index) => {
                                        // Gerçek index'i bul (Pagination yüzünden)
                                        const realIndex = indexOfFirstItem + index;
                                        return (
                                        <tr 
                                            key={product.id} 
                                            className={selectedProducts.includes(product.id) ? 'table-active' : ''}
                                            draggable={!searchTerm && !selectedCategory}
                                            onDragStart={(e) => onDragStart(e, realIndex)}
                                            onDragOver={(e) => onDragOver(e, realIndex)}
                                            onDrop={(e) => onDrop(e, realIndex)}
                                            style={{ cursor: (!searchTerm && !selectedCategory) ? 'move' : 'default' }}
                                        >
                                    <td className="text-muted"><FaGripVertical style={{ cursor: 'move' }} /></td>
                                    <td className="ps-4">
                                        <div className="form-check">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input"
                                            checked={selectedProducts.includes(product.id)}
                                            onChange={() => handleSelect(product.id)}
                                        />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{width: '50px', height: '50px', overflow: 'hidden'}}>
                                            {product.main_image && (
                                                <img 
                                                    src={`${FILE_URL}/${product.main_image}`} 
                                                    alt={product.name} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) || (
                                                <FaBoxOpen className="text-muted opacity-50" />
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fw-bold text-dark">{product.name}</div>
                                        <small className="text-muted">ID: #{product.id}</small>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-wrap gap-1">
                                            {product.categories && product.categories.map(cat => (
                                                <span key={cat.id} className="badge bg-light text-secondary border fw-normal">{cat.name}</span>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge bg-info-soft text-info">
                                            {product.unit_type === 'piece' ? t('common.unit_piece') : 
                                             product.unit_type === 'm2' ? t('common.unit_m2') : t('common.unit_pallet')}
                                        </span>
                                    </td>
                                    <td><span className="badge bg-secondary">{product.variation_count}</span></td>
                                    <td>
                                        <span className={`badge ${
                                            (product.total_stock || 0) <= 0 ? 'bg-danger-soft text-danger' : 
                                            (product.total_stock || 0) < 20 ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'
                                        }`}>
                                            {product.total_stock || 0}
                                        </span>
                                    </td>
                                    <td>
                                        <button 
                                            className={`btn btn-sm border-0 ${product.is_active ? 'text-success' : 'text-secondary'}`}
                                            onClick={() => handleStatusChange(product.id, product.is_active)}
                                            title={product.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                                            style={{ cursor: (!searchTerm && !selectedCategory) ? 'pointer' : 'default' }}
                                        >
                                            {product.is_active ? <FaCheckCircle size={20} /> : <FaTimesCircle size={20} />}
                                        </button>
                                    </td>
                                    <td className="text-end pe-4">
                                        <div className="btn-group">
                                            <Link to={`/admin/products/edit/${product.id}`} className="btn btn-sm btn-outline-primary" title={t('admin_products.edit')}>
                                                <FaEdit />
                                            </Link>
                                            <button onClick={() => handleDelete(product.id)} className="btn btn-sm btn-outline-danger" title={t('admin_products.delete')}>
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                    );
                                    })}
                                    {currentItems.length === 0 && (
                                        <tr>
                                            <td colSpan="9" className="text-center p-5">
                                                <div className="text-muted mb-2"><FaBoxOpen size={40} className="opacity-25"/></div>
                                                <p className="h6 text-muted">{t('admin_products.not_found')}</p>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Mobile: Card View */}
                <div className="d-lg-none">
                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">{t('common.loading')}</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {currentItems.length > 0 && (
                                <div className="p-3 border-bottom bg-light d-flex align-items-center">
                                    <div className="form-check">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input"
                                            onChange={handleSelectAll}
                                            checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                                        />
                                        <label className="form-check-label fw-bold small text-muted">
                                            {t('admin_products.select_all')}
                                        </label>
                                    </div>
                                </div>
                            )}
                            {currentItems.map(product => (
                                <div key={product.id} className={`p-3 border-bottom ${selectedProducts.includes(product.id) ? 'bg-light' : ''}`}>
                                    <div className="d-flex align-items-start gap-3">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input mt-2"
                                            checked={selectedProducts.includes(product.id)}
                                            onChange={() => handleSelect(product.id)}
                                        />
                                        <div className="flex-shrink-0">
                                            <img 
                                                src={product.main_image ? `${FILE_URL}/${product.main_image}` : ''} 
                                                alt={product.name} 
                                                className="rounded"
                                                style={{ width: '60px', height: '60px', objectFit: 'cover', backgroundColor: '#f8f9fa' }}
                                                onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 24 24' fill='none' stroke='%236c757d' stroke-width='1' stroke-linecap='round' stroke-linejoin='round' class='feather feather-box'%3E%3Cpath d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'%3E%3C/path%3E%3Cpolyline points='3.27 6.96 12 12.01 20.73 6.96'%3E%3C/polyline%3E%3Cline x1='12' y1='22.08' x2='12' y2='12'%3E%3C/line%3E%3C/svg%3E"; }}
                                            />
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between">
                                                <Link to={`/admin/products/edit/${product.id}`} className="fw-bold text-dark text-decoration-none mb-1">{product.name}</Link>
                                                <button 
                                                    className={`btn btn-sm border-0 p-0 ${product.is_active ? 'text-success' : 'text-secondary'}`}
                                                    onClick={() => handleStatusChange(product.id, product.is_active)}
                                                >
                                                    {product.is_active ? <FaCheckCircle size={18} /> : <FaTimesCircle size={18} />}
                                                </button>
                                            </div>
                                            <div className="small text-muted mb-2">
                                                {t('admin_products.total_stock')}: <span className="fw-bold">{product.total_stock || 0}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex flex-wrap gap-1">
                                                    {product.categories && product.categories.slice(0, 2).map(cat => (
                                                        <span key={cat.id} className="badge bg-light text-secondary border fw-normal">{cat.name}</span>
                                                    ))}
                                                </div>
                                                <div className="btn-group">
                                                    <Link to={`/admin/products/edit/${product.id}`} className="btn btn-sm btn-outline-primary" title={t('admin_products.edit')}><FaEdit /></Link>
                                                    <button onClick={() => handleDelete(product.id)} className="btn btn-sm btn-outline-danger" title={t('admin_products.delete')}><FaTrash /></button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Pagination Footer */}
                {!loading && filteredProducts.length > 0 && (
                    <div className="card-footer bg-white border-top-0 py-3 d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                            {t('products.total_items')} <strong>{filteredProducts.length}</strong> {t('products.items_from')} <strong>{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredProducts.length)}</strong> {t('products.showing_range')}.
                        </div>
                        {filteredProducts.length > itemsPerPage && (
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link border-0 rounded-circle mx-1" onClick={() => paginate(currentPage - 1)}>&laquo;</button>
                                    </li>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                            <button className="page-link border-0 rounded-circle mx-1" onClick={() => paginate(i + 1)}>
                                                {i + 1}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link border-0 rounded-circle mx-1" onClick={() => paginate(currentPage + 1)}>&raquo;</button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </div>
                )}
            </div>

        </AdminLayout>
    );
};

export default Products;